// ===========================================================================
// Main Application Component
// Crowd/Mob Detection Whitebox Transparency Demo
// ===========================================================================

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  WorldState,
  FrameData,
  PipelineThresholds,
  SimulationParams,
  UIState,
  InspectorTab,
  TrackerState,
  DEFAULT_THRESHOLDS,
  DEFAULT_SIM_PARAMS,
  WORLD_WIDTH,
  WORLD_HEIGHT,
} from './types';
import {
  createWorld,
  resetWorld,
  stepWorld,
  addPerson,
  addWeapon,
  removeAgent,
  moveAgent,
  spawnCrowd,
  spawnRandom,
  spawnLine,
  findNearestPerson,
} from './sim/world';
import { runDetector } from './sim/detector';
import {
  createTrackerState,
  updateTracker,
  updateSuspectLabels,
} from './sim/tracker';
import { computeDepthGrid } from './sim/depth';
import { runCrowdPipeline } from './sim/crowd';
import {
  SimulationCanvas,
  ControlPanel,
  Inspector,
  PipelineView,
} from './components';
import './App.css';

const TICK_INTERVAL = 1000 / 30; // 30 FPS

function App() {
  // World state
  const [world, setWorld] = useState<WorldState>(() => createWorld());

  // Pipeline thresholds
  const [thresholds, setThresholds] = useState<PipelineThresholds>(DEFAULT_THRESHOLDS);

  // Simulation parameters
  const [simParams, setSimParams] = useState<SimulationParams>(DEFAULT_SIM_PARAMS);

  // UI state
  const [uiState, setUIState] = useState<UIState>({
    inspectorTab: 'reasoning',
    selectedTrackId: null,
    showBoundingBoxes: false,
    showTrackIds: true,
    showTrackHistory: false,
    showContours: true,
    showDepthOverlay: false,
    showHeatmapOverlay: false,
  });

  // Frame data output
  const [frameData, setFrameData] = useState<FrameData | null>(null);

  // Tracker state (persists across frames)
  const trackerStateRef = useRef<TrackerState>(createTrackerState());

  // Suspect hysteresis state
  const suspectStateRef = useRef<Map<string, { nearFrames: number; pinFrames: number }>>(
    new Map()
  );

  // Heatmap history buffer
  const heatmapHistoryRef = useRef<number[][][]>([]);

  // Animation frame ref
  const animationRef = useRef<number | null>(null);
  const lastTickRef = useRef<number>(0);

  // Running state
  const [running, setRunning] = useState(false);

  // Run one simulation tick
  const tick = useCallback(() => {
    setWorld((prevWorld) => {
      // Step world physics
      const newWorld = stepWorld(prevWorld, 1, simParams.agentSpeed);

      // Run detection
      const detections = runDetector(
        newWorld.people,
        newWorld.weapons,
        simParams
      );

      // Update tracker
      const { state: newTrackerState, tracks: rawTracks } = updateTracker(
        trackerStateRef.current,
        detections,
        newWorld.frameIndex,
        30, // maxAge
        3   // minHits
      );
      trackerStateRef.current = newTrackerState;

      // Compute depth grid
      const depthGrid = computeDepthGrid(simParams);

      // Get weapon positions
      const weaponPositions = newWorld.weapons.map((w) => ({ x: w.x, y: w.y, id: w.id }));

      // Update suspect labels with hysteresis
      const tracksWithLabels = updateSuspectLabels(
        rawTracks,
        weaponPositions,
        thresholds.suspectProximity,
        thresholds.suspectConfirmFrames,
        thresholds.suspectPinFrames,
        suspectStateRef.current
      );

      // Run crowd pipeline
      const crowdResult = runCrowdPipeline(
        tracksWithLabels,
        depthGrid,
        weaponPositions,
        thresholds,
        heatmapHistoryRef.current
      );

      // Build frame data
      const newFrameData: FrameData = {
        frameIndex: newWorld.frameIndex,
        timestampMs: Date.now(),
        detections,
        tracks: tracksWithLabels,
        weapons: weaponPositions,
        gatherings: crowdResult.gatherings,
        depthGrid,
        heatmapGrid: crowdResult.heatmapGrid,
        binaryMask: crowdResult.binaryMask,
        crowdRegions: crowdResult.crowdRegions,
        flags: crowdResult.flags,
        metrics: {
          peopleCount: newWorld.people.length,
          confirmedTracks: tracksWithLabels.filter((t) => t.confirmed).length,
          gatheringCount: crowdResult.gatherings.length,
          contourCount: crowdResult.contours.length,
          thresholds,
        },
        reasoning: crowdResult.reasoning,
      };

      setFrameData(newFrameData);
      return newWorld;
    });
  }, [simParams, thresholds]);

  // Animation loop
  useEffect(() => {
    if (!running) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    const loop = (timestamp: number) => {
      if (timestamp - lastTickRef.current >= TICK_INTERVAL) {
        tick();
        lastTickRef.current = timestamp;
      }
      animationRef.current = requestAnimationFrame(loop);
    };

    animationRef.current = requestAnimationFrame(loop);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [running, tick]);

  // Event handlers
  const handleAddPerson = useCallback((x: number, y: number) => {
    setWorld((prev) => addPerson(prev, x, y));
  }, []);

  const handleAddWeapon = useCallback((x: number, y: number) => {
    setWorld((prev) => addWeapon(prev, x, y, false));
  }, []);

  const handleRemoveAgent = useCallback((id: string) => {
    setWorld((prev) => removeAgent(prev, id));
  }, []);

  const handleSelectAgent = useCallback((id: string | null) => {
    setWorld((prev) => ({ ...prev, selectedAgentId: id }));
  }, []);

  const handleMoveAgent = useCallback((id: string, x: number, y: number) => {
    setWorld((prev) => moveAgent(prev, id, x, y));
  }, []);

  const handleDragStart = useCallback((id: string, offsetX: number, offsetY: number) => {
    setWorld((prev) => ({
      ...prev,
      selectedAgentId: id,
      dragOffset: { x: offsetX, y: offsetY },
    }));
  }, []);

  const handleDragEnd = useCallback(() => {
    setWorld((prev) => ({ ...prev, dragOffset: null }));
  }, []);

  const handleSpawnCrowd = useCallback(() => {
    const cx = WORLD_WIDTH / 2 + (Math.random() - 0.5) * 400;
    const cy = WORLD_HEIGHT / 2 + (Math.random() - 0.5) * 200;
    setWorld((prev) => spawnCrowd(prev, cx, cy, 8 + Math.floor(Math.random() * 5), 80));
  }, []);

  const handleSpawnLine = useCallback(() => {
    const y = 200 + Math.random() * 300;
    setWorld((prev) => spawnLine(prev, 200, y, 1080, y, 6));
  }, []);

  const handleSpawnRandom = useCallback(() => {
    setWorld((prev) => spawnRandom(prev, 5));
  }, []);

  const handleAddWeaponToNearest = useCallback(() => {
    setWorld((prev) => {
      const centerX = WORLD_WIDTH / 2;
      const centerY = WORLD_HEIGHT / 2;
      const nearest = findNearestPerson(prev, centerX, centerY);
      if (nearest) {
        return addWeapon(prev, nearest.x, nearest.y, true);
      }
      return addWeapon(prev, centerX, centerY, false);
    });
  }, []);

  const handleReset = useCallback(() => {
    setWorld(resetWorld());
    trackerStateRef.current = createTrackerState();
    suspectStateRef.current.clear();
    heatmapHistoryRef.current = [];
    setFrameData(null);
  }, []);

  const handleToggleRunning = useCallback(() => {
    setRunning((prev) => !prev);
  }, []);

  const handleStep = useCallback(() => {
    tick();
  }, [tick]);

  const handleTabChange = useCallback((tab: InspectorTab) => {
    setUIState((prev) => ({ ...prev, inspectorTab: tab }));
  }, []);

  const handleSelectTrack = useCallback((trackId: number | null) => {
    setUIState((prev) => ({ ...prev, selectedTrackId: trackId }));
  }, []);

  return (
    <div className="app">
      {/* Header */}
      <header className="app-header">
        <div className="header-title">
          <h1>🎯 Crowd/Mob Detection</h1>
          <span className="subtitle">Whitebox Transparency Demo</span>
        </div>
        <div className="header-badges">
          {frameData?.flags.mob && (
            <span className="header-badge badge-mob">🚨 MOB ALERT</span>
          )}
          {frameData?.flags.crowd && !frameData?.flags.mob && (
            <span className="header-badge badge-crowd">⚠️ CROWD</span>
          )}
          {frameData?.flags.weaponGlobal && (
            <span className="header-badge badge-weapon">🔫 WEAPON</span>
          )}
          {!frameData?.flags.crowd && !frameData?.flags.mob && (
            <span className="header-badge badge-safe">✅ CLEAR</span>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="app-content">
        {/* Left Panel: Controls */}
        <aside className="panel-left">
          <ControlPanel
            thresholds={thresholds}
            simParams={simParams}
            uiState={uiState}
            running={running}
            onThresholdsChange={setThresholds}
            onSimParamsChange={setSimParams}
            onUIStateChange={setUIState}
            onSpawnCrowd={handleSpawnCrowd}
            onSpawnLine={handleSpawnLine}
            onSpawnRandom={handleSpawnRandom}
            onAddWeaponToNearest={handleAddWeaponToNearest}
            onReset={handleReset}
            onToggleRunning={handleToggleRunning}
            onStep={handleStep}
          />
        </aside>

        {/* Center: Simulation Canvas */}
        <main className="panel-center">
          <SimulationCanvas
            world={world}
            frameData={frameData}
            uiState={uiState}
            onAddPerson={handleAddPerson}
            onAddWeapon={handleAddWeapon}
            onRemoveAgent={handleRemoveAgent}
            onSelectAgent={handleSelectAgent}
            onMoveAgent={handleMoveAgent}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          />
        </main>

        {/* Right Panel: Inspector + Pipeline */}
        <aside className="panel-right">
          <div className="panel-right-section pipeline-section">
            <PipelineView frameData={frameData} />
          </div>
          <div className="panel-right-section inspector-section">
            <Inspector
              frameData={frameData}
              uiState={uiState}
              onTabChange={handleTabChange}
              onSelectTrack={handleSelectTrack}
            />
          </div>
        </aside>
      </div>

      {/* Footer */}
      <footer className="app-footer">
        <span>Interactive Simulation • No Video • No ML Inference • Pure TypeScript</span>
        <span className="footer-link">
          <a href="https://github.com" target="_blank" rel="noopener noreferrer">
            View on GitHub
          </a>
        </span>
      </footer>
    </div>
  );
}

export default App;
